/**
 * Token-bound text field for monograph demo panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import type { MonographPanelTokens } from "./MonographPanelPrimitiveTypes.js";

export interface MonographTextFieldProps {
  readonly tokens: MonographPanelTokens;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly ariaLabel: string;
  readonly type?: "search" | "text";
  readonly label?: React.ReactNode;
  readonly placeholder?: string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
  readonly inputClassName?: string;
  readonly inputStyle?: React.CSSProperties;
  readonly color?: string;
  readonly fullWidth?: boolean;
  readonly minWidth?: React.CSSProperties["minWidth"];
  readonly mono?: boolean;
}

export function MonographTextField({
  tokens,
  value,
  onChange,
  ariaLabel,
  type = "text",
  label,
  placeholder,
  className,
  style,
  inputClassName,
  inputStyle,
  color = tokens.accent,
  fullWidth = false,
  minWidth = 220,
  mono = false,
}: MonographTextFieldProps): React.JSX.Element {
  return (
    <label
      className={cn("csk-monograph-text-field", className)}
      style={{
        alignItems: "center",
        background: colorWithAlpha(tokens.void, "70"),
        border: `1px solid ${tokens.border}`,
        borderRadius: 8,
        color: tokens.muted,
        display: "inline-flex",
        flex: fullWidth ? "1 1 auto" : undefined,
        fontFamily: tokens.mono,
        fontSize: 12,
        fontWeight: 700,
        gap: 8,
        minWidth: fullWidth ? minWidth : undefined,
        padding: "6px 10px",
        transition: "border-color 140ms ease, box-shadow 140ms ease",
        width: fullWidth ? "100%" : undefined,
        ...style,
      }}
      onFocus={(event) => {
        event.currentTarget.style.borderColor = colorWithAlpha(color, "aa");
        event.currentTarget.style.boxShadow = `0 0 12px ${colorWithAlpha(color, "24")}`;
      }}
      onBlur={(event) => {
        event.currentTarget.style.borderColor = tokens.border;
        event.currentTarget.style.boxShadow = "none";
      }}
    >
      {label ? <span>{label}</span> : null}
      <input
        type={type}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={cn("csk-monograph-text-field__input", inputClassName)}
        style={{
          background: "transparent",
          border: 0,
          color: tokens.bright,
          flex: "1 1 auto",
          fontFamily: mono ? tokens.mono : undefined,
          fontSize: 13,
          minWidth,
          outline: 0,
          ...inputStyle,
        }}
      />
    </label>
  );
}
